import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DateRange from "@/components/DateRange";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";
import { toast } from "sonner";
import {
  ArrowLeft,
  Copy,
  Crown,
  Loader2,
  UserPlus,
  Trophy,
  Trash2,
  LogOut,
} from "lucide-react";

const GREEN = "#34C759";
const RED = "#FF3B30";

function Kpi({ label, value, tone, testid }) {
  const c = tone === "up" ? "text-[#34C759]" : tone === "down" ? "text-[#FF3B30]" : "text-foreground";
  return (
    <div className="glass rounded-3xl p-5" data-testid={testid}>
      <div className="text-sm font-medium text-muted-foreground mb-2">
        {label}
      </div>
      <div className={`font-display text-2xl sm:text-3xl font-extrabold ${c}`}>{value}</div>
    </div>
  );
}

export default function GroupDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("pooled");
  const [range, setRange] = useState({ start: "", end: "", preset: "all" });
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (range.start) params.start = range.start;
    if (range.end) params.end = range.end;
    try {
      const [d, a, b] = await Promise.all([
        api.get(`/groups/${id}`),
        api.get(`/groups/${id}/analytics`, { params }),
        api.get(`/groups/${id}/bets`, { params }),
      ]);
      setDetail(d.data);
      setAnalytics(a.data);
      setBets(b.data);
    } catch (e) {
      toast.error("Could not load group");
      navigate("/groups");
    } finally {
      setLoading(false);
    }
  }, [id, range, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const copyCode = () => {
    navigator.clipboard.writeText(detail.invite_code);
    toast.success("Invite code copied");
  };

  const invite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const { data } = await api.post(`/groups/${id}/invite`, { email: inviteEmail });
      toast.success(data.message);
      setInviteEmail("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setInviting(false);
    }
  };

  const removeGroup = async () => {
    try {
      await api.delete(`/groups/${id}`);
      toast.success("Group deleted");
      navigate("/groups");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const leaveGroup = async () => {
    try {
      await api.delete(`/groups/${id}/leave`);
      toast.success("Left group");
      navigate("/groups");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (loading || !detail || !analytics) {
    return (
      <div className="h-64 flex items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }

  const s = analytics.pooled_summary;
  const markets = analytics.pooled_trends.by_market || [];
  const categories = analytics.pooled_trends.by_category || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/groups")}
          className="rounded-full"
          data-testid="group-back"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight truncate">
            {detail.name}
          </h1>
          <button
            onClick={copyCode}
            data-testid="copy-invite-code"
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5 mt-0.5"
          >
            Invite code: <span className="font-mono font-semibold">{detail.invite_code}</span>
            <Copy className="h-3 w-3" />
          </button>
        </div>
        {detail.is_owner ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full text-muted-foreground hover:text-destructive" data-testid="delete-group">
                <Trash2 className="h-5 w-5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-3xl">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this group?</AlertDialogTitle>
                <AlertDialogDescription>
                  Members will lose access to the shared view. Their own bets are not deleted.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={removeGroup} data-testid="confirm-delete-group" className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : (
          <Button variant="ghost" size="icon" className="rounded-full text-muted-foreground hover:text-destructive" onClick={leaveGroup} data-testid="leave-group">
            <LogOut className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* Members + invite */}
      <div className="glass rounded-3xl p-5" data-testid="group-members">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {detail.members.map((m) => (
            <Badge
              key={m.id}
              variant="outline"
              className="rounded-full py-1 pl-1 pr-3 gap-1.5 bg-secondary/50"
            >
              <span className="h-6 w-6 rounded-full bg-primary/15 text-primary flex items-center justify-center text-[10px] font-bold">
                {(m.name || m.email)[0]?.toUpperCase()}
              </span>
              {m.name || m.email}
              {m.id === detail.owner_id && <Crown className="h-3 w-3 text-[#FF9500]" />}
            </Badge>
          ))}
        </div>
        {detail.is_owner && (
          <div className="flex gap-2">
            <Input
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="Invite by email (registered user)"
              data-testid="invite-email-input"
              className="rounded-full h-10"
            />
            <Button onClick={invite} disabled={inviting} className="rounded-full shrink-0" data-testid="invite-submit">
              {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              Invite
            </Button>
          </div>
        )}
      </div>

      {/* View toggle + date range */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex gap-1 bg-secondary/60 rounded-full p-1 w-fit">
          {[
            { k: "pooled", l: "Pooled" },
            { k: "members", l: "By Member" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setView(t.k)}
              data-testid={`view-${t.k}`}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                view === t.k ? "bg-card shadow-sm text-foreground" : "text-muted-foreground"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>
        <DateRange range={range} setRange={setRange} />
      </div>

      {view === "pooled" ? (
        s.total_bets === 0 ? (
          <div className="glass rounded-3xl p-12 text-center text-muted-foreground" data-testid="pooled-empty">
            No bets shared in this group yet.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Kpi label="Group P/L" value={money(s.profit)} tone={s.profit >= 0 ? "up" : "down"} testid="group-kpi-profit" />
              <Kpi label="ROI" value={`${s.roi}%`} tone={s.roi >= 0 ? "up" : "down"} testid="group-kpi-roi" />
              <Kpi label="Win Rate" value={`${s.win_rate}%`} testid="group-kpi-winrate" />
              <Kpi label="Total Bets" value={s.total_bets} testid="group-kpi-total" />
            </div>

            <div className="glass rounded-3xl p-5 sm:p-6" data-testid="group-market-breakdown">
              <h3 className="font-display font-bold text-lg mb-4">Profit by Market</h3>
              {markets.length ? (
                <ResponsiveContainer width="100%" height={Math.max(200, markets.length * 46)}>
                  <BarChart data={markets} layout="vertical" margin={{ left: 8, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                    <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: "hsl(var(--foreground))" }} tickLine={false} axisLine={false} />
                    <Tooltip cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} formatter={(v) => money(v)} />
                    <Bar dataKey="profit" name="Profit" radius={[0, 8, 8, 0]}>
                      {markets.map((m, i) => (
                        <Cell key={i} fill={m.profit >= 0 ? GREEN : RED} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted-foreground text-sm py-8 text-center">No settled bets.</p>
              )}
            </div>

            {categories.length > 0 && (
              <div className="glass rounded-3xl p-5 sm:p-6" data-testid="group-category-breakdown">
                <h3 className="font-display font-bold text-lg mb-4">Profit by Competition</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {categories.map((c) => (
                    <div key={c.name} className="flex items-center justify-between rounded-2xl bg-secondary/50 px-4 py-3">
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{c.name}</div>
                        <div className="text-xs text-muted-foreground">{c.bets} bets · {c.win_rate}% win</div>
                      </div>
                      <div className={`font-display font-bold ${c.profit > 0 ? "text-[#34C759]" : c.profit < 0 ? "text-[#FF3B30]" : "text-muted-foreground"}`}>
                        {c.profit > 0 ? "+" : ""}{money(c.profit)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )
      ) : (
        <div className="space-y-3" data-testid="member-leaderboard">
          {analytics.members.map((m, i) => (
            <motion.div
              key={m.user_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="glass rounded-2xl p-4 flex items-center gap-4"
              data-testid="member-stat-row"
            >
              <div className="h-9 w-9 rounded-full bg-primary/15 text-primary flex items-center justify-center font-bold shrink-0">
                {i === 0 ? <Trophy className="h-4 w-4 text-[#FF9500]" /> : i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">
                  {m.name} {m.user_id === user?.id && <span className="text-xs text-muted-foreground">(you)</span>}
                </div>
                <div className="text-xs text-muted-foreground">
                  {m.total_bets} bets · {m.win_rate}% win · {m.roi}% ROI
                </div>
              </div>
              <div className={`font-display font-bold text-lg ${m.profit > 0 ? "text-[#34C759]" : m.profit < 0 ? "text-[#FF3B30]" : "text-muted-foreground"}`}>
                {m.profit > 0 ? "+" : ""}{money(m.profit)}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Shared bets (view only) */}
      <div className="space-y-2">
        <h3 className="font-display font-bold text-lg">Shared Bets <span className="text-sm font-normal text-muted-foreground">(view only)</span></h3>
        {bets.length === 0 ? (
          <p className="text-muted-foreground text-sm py-4">No bets to show.</p>
        ) : (
          <div className="space-y-2" data-testid="group-bets-list">
            {bets.map((b) => (
              <div key={b.id} className="glass rounded-2xl p-4 flex items-center gap-3" data-testid="group-bet-row">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold truncate">{b.match || b.selection || b.market || "Bet"}</span>
                    <Badge variant="outline" className="text-[11px] rounded-full bg-secondary/60">
                      {b.owner_name}
                    </Badge>
                    {b.category && (
                      <Badge variant="outline" className="text-[11px] rounded-full bg-primary/10 text-primary border-primary/20">
                        {b.category}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate mt-0.5">
                    {[b.market, b.selection].filter(Boolean).join(" · ")} · {b.bet_date} · @{b.odds}
                  </div>
                </div>
                <div className={`font-display font-bold shrink-0 ${b.profit > 0 ? "text-[#34C759]" : b.profit < 0 ? "text-[#FF3B30]" : "text-muted-foreground"}`}>
                  {b.profit > 0 ? "+" : ""}{money(b.profit)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
